import type React from 'react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Alert, Box, Card, Heading, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import { ReviewRenderer } from '@zillow/cosmos';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { useRecoilState } from 'recoil';
import { useIsFetching } from '@zg-rentals/react-query5';
import { isSigningCompleteModalOpenState } from '../../../atoms';
import TenantSigningCompleteModal from '../LandlordLeaseView/ListView/LeaseSignPreview/TenantSigningCompleteModal';

import Disclaimer from '../../components/Widgets/Disclaimer';
import { useLeasingUrlParams, useTenant } from '../../hooks';
import {
  getCosmosJourneyToken,
  useAttachedLeaseAcceptanceForUserQuery,
  useDisclaimerAcceptanceForUserQuery,
  useLandlordProvidedDocumentsQuery,
  useLeaseQuery,
  useLeaseSummaryQuery,
  usePropertyInfoQuery,
  useSetAttachedLeaseAcceptanceForUserMutation,
  useSetDisclaimerAcceptanceForUserMutation,
} from '../../utils/queries';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import { DocumentBackend } from '../../models/Types';
import { SUMMARY_VIEW_SUBTYPES } from '../../models/Lease';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';
import LandlordProvidedDocuments from '../../components/Widgets/LandlordProvidedDocuments';
import { LEASE_SUMMARY_CHECKBOX_LABELS } from '../TenantDashboard/constants';
import UserInfoVerifier from '../../components/UserInfoVerifier';
import DocumentPreviewSelector from '../../components/DocumentPreviewSelector';
import LeaseAttachedCheckbox from '../../components/Widgets/LeaseAttachedCheckbox';
import SummaryReadOnly from '../../components/LeaseSummary/SummaryReadOnly';
import LeaseExtension from '../../models/LeaseExtension';
import { LeaseAction, Property, Summary } from '../../models';

function TenantLeaseSummary() {
  const history = useHistory();
  const { leaseId, nestedViewType } = useLeasingUrlParams();
  const { tenant } = useTenant();
  const { enqueueToast } = useToast();

  // React Query hooks
  const leaseQuery = useLeaseQuery(leaseId, { enabled: !!leaseId });
  const summaryQueryData = useLeaseSummaryQuery(leaseId, LeaseArtifactType.LEASE, {
    enabled: !!leaseId,
  });
  const propertyQuery = usePropertyInfoQuery(leaseQuery.data?.listingAlias || '', !!leaseQuery.data?.listingAlias);
  const landlordProvidedDocumentsQuery = useLandlordProvidedDocumentsQuery(leaseId, {
    enabled: !!leaseId,
  });
  const disclaimerAcceptanceQuery = useDisclaimerAcceptanceForUserQuery(leaseId);
  const attachedLeaseAcceptanceQuery = useAttachedLeaseAcceptanceForUserQuery(leaseId);

  const setAttachedLeaseAcceptanceMutation = useSetAttachedLeaseAcceptanceForUserMutation();
  const setDisclaimerAcceptanceMutation = useSetDisclaimerAcceptanceForUserMutation();

  // Data from React Query
  const lease = leaseQuery.data;
  const property = propertyQuery.data ? new Property(propertyQuery.data) : null;
  const summaryData = summaryQueryData.data;
  const summary = summaryData?.summary ? new Summary({ sectionCategories: summaryData.summary }) : null;
  const summaryText = summaryData?.summaryText || '';
  const summaryActions =
    summaryData?.summaryActions?.map(
      (params: { name: string; disabled?: boolean }) => new LeaseAction(params.name, params.disabled),
    ) || [];
  const landlordProvidedDocuments = (landlordProvidedDocumentsQuery.data?.documentNames || []).map(
    (doc: string | { text: string }) => {
      // If doc is already an object with text property, use it as is
      if (typeof doc === 'object' && doc !== null && 'text' in doc) {
        return doc;
      }
      // If doc is a string, wrap it in the expected format
      return { text: String(doc) };
    },
  );
  // Keep as undefined if not loaded yet, so we can check != null in renderHeader
  const acceptedDisclaimer = disclaimerAcceptanceQuery.isLoading
    ? undefined
    : (disclaimerAcceptanceQuery.data?.acceptedDisclaimer ?? false);
  const acceptedAttachedLease = attachedLeaseAcceptanceQuery.isLoading
    ? false
    : (attachedLeaseAcceptanceQuery.data?.acceptedAttachedLease ?? false);

  const [canClickCta, setCanClickCta] = useState(false);
  const [acceptedAttachedLeaseCheckbox, setAcceptedAttachedLeaseCheckbox] = useState(false);
  const [isReviewRendererLoading, setIsReviewRendererLoading] = useState(false);
  const [hasRenderedReviewRenderer, setHasRenderedReviewRenderer] = useState(false);
  const [isAcceptingDisclaimer, setIsAcceptingDisclaimer] = useState(false);

  const [isSigningCompleteModalOpen, setIsSigningCompleteModalOpen] = useRecoilState(isSigningCompleteModalOpenState);

  const isFetching = useIsFetching();
  const latestExtension = lease?.latestExtension ?? null;

  useEffect(() => {
    setIsSigningCompleteModalOpen(false);
  }, [setIsSigningCompleteModalOpen]);

  useEffect(() => {
    setAcceptedAttachedLeaseCheckbox(acceptedAttachedLease);
  }, [acceptedAttachedLease]);

  // Track when we're rendering a ReviewRenderer so we know to wait for it to load
  useEffect(() => {
    if (lease?.journeyId) {
      setHasRenderedReviewRenderer(true);
      setIsReviewRendererLoading(true);
    }
  }, [lease?.journeyId]);

  function onCloseLeasePreview(): void {
    if (!lease) return;
    history.push(lease.getSummaryPath());
    // React Query will automatically refetch via invalidations
  }

  function onLeasePreviewError(notification: string): void {
    onCloseLeasePreview();
    enqueueToast(<Toast tone="critical">{notification}</Toast>);
  }

  function onToggleCanClickCta(): void {
    setCanClickCta(!canClickCta);
  }

  async function onCtaClicked() {
    if (!lease || isAcceptingDisclaimer) return;

    setIsAcceptingDisclaimer(true);

    const promises: Array<Promise<unknown>> = [];

    if (acceptedAttachedLeaseCheckbox) {
      promises.push(
        setAttachedLeaseAcceptanceMutation.mutateAsync({ postData: { leaseId: lease.id as unknown as number } }),
      );
    }

    if (canClickCta) {
      promises.push(
        setDisclaimerAcceptanceMutation.mutateAsync({ postData: { leaseId: lease.id as unknown as number } }),
      );
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }

    setIsAcceptingDisclaimer(false);
  }

  function renderPrimaryAction(disabled?: boolean): React.ReactNode {
    return summaryActions ? (
      <LeaseActionButtonGroup
        leaseActions={summaryActions}
        fluid
        lease={lease}
        property={property}
        disabled={disabled || isAcceptingDisclaimer}
        data-test="lease-summary-action-button"
        onPrimaryActionClick={acceptedDisclaimer ? null : onCtaClicked}
      />
    ) : null;
  }

  function onToggleAttachedLeaseAccepted() {
    setAcceptedAttachedLeaseCheckbox(!acceptedAttachedLeaseCheckbox);
  }

  function renderDisclaimer(): React.ReactNode {
    if (lease == null) {
      return null;
    }

    const hasDocuments = landlordProvidedDocuments.length > 0;
    const isOfflineSigning = lease && lease.status === LeaseStatus.SENT_FOR_OFFLINE_SIGNING;
    const disclaimerText = isOfflineSigning
      ? LEASE_SUMMARY_CHECKBOX_LABELS.OFFLINE_SIGNING
      : LEASE_SUMMARY_CHECKBOX_LABELS.ONLINE_SIGNING;
    const checkboxLabel = hasDocuments ? disclaimerText.WITH_DOCUMENTS : disclaimerText.WITH_NO_DOCUMENTS;

    return (
      <Disclaimer
        beforeCheckboxContent={
          hasDocuments && (
            <>
              <Heading level={3} css={{ textStyle: 'responsive.body-lg-bold' }}>
                Did you get {landlordProvidedDocuments.length === 1 ? 'this document' : 'these documents'}?
              </Heading>
              <Paragraph>
                {lease.documentBackend === DocumentBackend.dotloop
                  ? `Be sure to review ${landlordProvidedDocuments.length === 1 ? 'it' : 'them'} before signing your lease, or contact your landlord if you didn't receive the ${landlordProvidedDocuments.length === 1 ? 'document' : 'documents'}.`
                  : `Contact your landlord if you don't see the ${landlordProvidedDocuments.length === 1 ? 'document' : 'documents'} in your lease package.`}
              </Paragraph>
              <LandlordProvidedDocuments
                landlordProvidedDocuments={landlordProvidedDocuments}
                hideExcessDocuments={false}
                showIncludedText={false}
                showAsOrderedList
              />
            </>
          )
        }
        label="I agree to these terms before viewing"
        isChecked={canClickCta}
        onClick={onToggleCanClickCta}
        checkboxLabel={checkboxLabel}
        afterCheckboxContent={
          <>
            {' '}
            <LeaseAttachedCheckbox checked={acceptedAttachedLeaseCheckbox} onClick={onToggleAttachedLeaseAccepted} />
            {renderPrimaryAction(!canClickCta)}
          </>
        }
      />
    );
  }

  function onViewRenewalAgreement() {
    history.push(LeaseExtension.getWizardPath(leaseId, String(latestExtension?.id ?? '')));
  }

  function renderDisclaimerContainer() {
    if (lease == null || landlordProvidedDocumentsQuery.isLoading) {
      return (
        <>
          <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="40%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <Box css={{ marginBottom: 'tight' }}>
              <LoadingText isLoading loadingWidth="50%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="45%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tight' }}>
                Loading
              </LoadingText>
            </Box>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="50%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <Box css={{ marginBottom: 'tight' }}>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="100%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="95%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
              <LoadingText isLoading loadingWidth="85%" css={{ marginBottom: 'tight' }}>
                Loading
              </LoadingText>
            </Box>
            <LoadingText isLoading loadingWidth="70%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="45%"
              css={{ marginBottom: 'tighter', textStyle: 'responsive.body-lg-bold' }}
            >
              Loading
            </LoadingHeading>
            <LoadingText isLoading loadingWidth="75%" css={{ marginBottom: 'tight' }}>
              Loading
            </LoadingText>
            <LoadingButton isLoading>View full lease</LoadingButton>
          </Card>
        </>
      );
    }

    return <>{!acceptedDisclaimer ? renderDisclaimer() : renderPrimaryAction(false)}</>;
  }

  function renderHeader() {
    const propertyAddress = property?.streetAddress;
    const personFirstName = tenant?.firstName;
    const leaseStartDate = lease?.getFriendlyStartDate();

    const shouldShowSkeleton =
      !lease ||
      (acceptedDisclaimer === undefined && landlordProvidedDocumentsQuery.isLoading) ||
      (acceptedDisclaimer === false && landlordProvidedDocumentsQuery.isLoading);

    if (shouldShowSkeleton) {
      return (
        <Box css={{ marginBottom: 'tight' }}>
          <LoadingHeading
            isLoading
            level={2}
            loadingWidth="60%"
            css={{ textStyle: 'responsive.heading-xs', marginBottom: 'tighter' }}
          >
            Loading
          </LoadingHeading>
          <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingText>
          {renderDisclaimerContainer()}
        </Box>
      );
    }

    return (
      <>
        {latestExtension && (
          <Alert
            tone="info"
            actionButton={<TextButton onClick={onViewRenewalAgreement}>View renewal agreement</TextButton>}
            css={{ marginBottom: 'tight' }}
          >
            Your lease was renewed
          </Alert>
        )}
        <SummaryTitle {...{ summaryText, propertyAddress, personFirstName, leaseStartDate }} />
        {acceptedDisclaimer != null && <Box css={{ marginBottom: 'tight' }}>{renderDisclaimerContainer()}</Box>}
      </>
    );
  }

  function generateJourneyToken() {
    return getCosmosJourneyToken(LeaseArtifactType.LEASE, lease.id);
  }

  function renderSummary() {
    const showSkeleton =
      !lease ||
      (lease && !lease.journeyId && !summary) ||
      (lease?.journeyId && (!hasRenderedReviewRenderer || isReviewRendererLoading)) ||
      isFetching > 0;

    const skeleton = (
      <>
        {[1, 2, 3, 4, 5].map((index) => (
          <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter' }}>
            <LoadingHeading
              isLoading
              level={3}
              loadingWidth="40%"
              css={{ marginBottom: 'loose', textStyle: 'responsive.heading-xs' }}
            >
              Loading
            </LoadingHeading>
            {[1, 2, 3].map((rowIndex) => (
              <Box key={rowIndex} css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter' }}>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
                  <LoadingText isLoading loadingWidth="50%">
                    Loading
                  </LoadingText>
                </Box>
                <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
                  <LoadingText isLoading loadingWidth="70%">
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tighter' }}>
                    Loading
                  </LoadingText>
                  <LoadingText isLoading loadingWidth="60%">
                    Loading
                  </LoadingText>
                </Box>
              </Box>
            ))}
          </Card>
        ))}
      </>
    );

    // If we only need to show skeleton and no actual content needs to be mounted
    if (!lease || (lease && !lease.journeyId && !summary)) {
      return skeleton;
    }

    // Render both skeleton and actual content, hiding the content until loaded
    return (
      <>
        {showSkeleton && skeleton}
        <Box css={{ visibility: showSkeleton ? 'hidden' : 'visible', height: showSkeleton ? '0' : 'auto' }}>
          {lease.journeyId ? (
            <ReviewRenderer
              journeyId={lease.journeyId}
              journeyVersionId={lease.journeyVersionId}
              generateJourneyToken={generateJourneyToken}
              isReadOnly
              setIsReviewRendererLoading={setIsReviewRendererLoading}
            />
          ) : (
            <SummaryReadOnly lease={lease} summary={summary} />
          )}
          {acceptedDisclaimer != null && <Box css={{ marginTop: 'tight' }}>{renderDisclaimerContainer()}</Box>}
        </Box>
      </>
    );
  }

  function renderNestedView(): React.ReactNode {
    switch (nestedViewType) {
      case SUMMARY_VIEW_SUBTYPES.PREVIEW:
        return (
          <DocumentPreviewSelector
            leaseId={leaseId}
            lease={lease}
            property={property}
            onClose={onCloseLeasePreview}
            onError={onLeasePreviewError}
          />
        );
      default:
        return null;
    }
  }

  function handleCloseSigningModal() {
    setIsSigningCompleteModalOpen(false);
  }

  return (
    <>
      <UserInfoVerifier leaseId={leaseId} />
      {lease && (
        <TenantSigningCompleteModal
          isOpen={isSigningCompleteModalOpen}
          lease={lease}
          onClose={handleCloseSigningModal}
        />
      )}
      <>
        {renderHeader()}
        {renderSummary()}
        {renderNestedView()}
      </>
    </>
  );
}

export default TenantLeaseSummary;
